import {AddEditDocument} from "app/employee/incdoc/AddEditDocument";
import {forwardRef, type PropsWithChildren, useCallback, useImperativeHandle, useRef} from "react";
import {useImmer} from "use-immer";
import type {SearchResultIncomingDocument} from "app/employee/state/incdoc/mutations";
import {FstIonModal} from "app/employee/incdoc/FstIonModal";
import {useAttachmentCollectionEditor} from "app/components/hooks/useAttachmentCollectionEditor";

export interface DocumentUploaderHandle {
  showFileUpload(): void;
  editDocument(document: SearchResultIncomingDocument): void;
}

interface Props {
  onCompleted?: () => void;
}

export const DocumentUploader = forwardRef<DocumentUploaderHandle, PropsWithChildren<Props>>(({onCompleted}, ref) => {
  const modal = useRef<HTMLIonModalElement>(null);
  const [editDocument, setEditDocument] = useImmer<SearchResultIncomingDocument | null>(null);
  const {showFileUpload, unifiedAttachments, uploadProgress, updateAttachments, cleanup, collectOcrFiles} =
    useAttachmentCollectionEditor(editDocument?.attachmentCollection, () => modal.current?.present());

  const dismissDialog = useCallback(() => {
    cleanup();
    setEditDocument(null);

    modal.current?.dismiss();
    onCompleted?.();
  }, [cleanup, onCompleted, setEditDocument]);

  useImperativeHandle(
    ref,
    () => ({
      showFileUpload,
      editDocument: (document: SearchResultIncomingDocument) => {
        setEditDocument(() => document);
        modal.current?.present();
      }
    }),
    [setEditDocument, showFileUpload]
  );

  return (
    <FstIonModal onIonModalDidDismiss={dismissDialog} ref={modal}>
      <AddEditDocument
        collectOcrFiles={collectOcrFiles}
        incomingDocument={editDocument}
        showFileUpload={showFileUpload}
        unifiedAttachments={unifiedAttachments}
        updateAttachments={updateAttachments}
        uploadProgress={uploadProgress}
      />
    </FstIonModal>
  );
});

DocumentUploader.displayName = "DocumentUploader";
